import { Box, Popover, Tooltip } from '@mui/material';
import { IconButton, Typography, Dialog } from '@sm-dashboard/ui-components';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState } from 'react';
import { useAppContext } from '@/context/context';
import { useFetch } from '@/hooks/useFetch';
import { pageHelpUrl } from '@/constants';
import { EditorComponent } from '@sm-dashboard/form-provider';

const PageTitleHeader = () => {
  const { userPermission, setSnackbar, selectedNav } = useAppContext();
  const { fetchData, loading } = useFetch();

  // help state - כל ה-hooks בתחילת הקומפוננטה
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [help, setHelp] = useState<{ title?: string; content?: string } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');

  // Use selectedNav.link instead of location.pathname
  const normalizedPath = selectedNav?.link || '/';

  // העבר את הפונקציה loadHelp לפני useEffect
  const loadHelp = async () => {
    try {
      const data = await fetchData({
        url: `${pageHelpUrl}?path=${encodeURIComponent(normalizedPath)}`,
        method: 'GET'
      });
      setHelp(data || null);
    } catch (error) {
      // Silent fail - no help available
    }
  };

  // עכשיו useEffect יכול להשתמש ב-loadHelp
  useEffect(() => {
    loadHelp();
  }, [normalizedPath]);

  // בדיקת תנאי אחרי כל ה-hooks
  const pagesWithLocalTitle: string[] = [];
  if (pagesWithLocalTitle.some(page => selectedNav?.link?.includes(page))) {
    return null;
  }

  // Use selectedNav.primary instead of getPageName
  const pageTitle = selectedNav?.primary || '';

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  const onEditClick = () => {
    setEditTitle(help?.title || pageTitle);
    setEditContent(help?.content || '');
    setEditOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = { path: normalizedPath, title: editTitle, content: editContent };
      await fetchData({ url: pageHelpUrl, method: 'POST', data: payload });
      setSnackbar({ open: true, severity: 'success', message: 'ההסבר נשמר בהצלחה' });
      setEditOpen(false);
      await loadHelp();
    } catch (error) {
      setSnackbar({ open: true, severity: 'error', message: 'שמירה נכשלה' });
    }
  };

  const handleDelete = async () => {
    try {
      await fetchData({
        url: `${pageHelpUrl}?path=${encodeURIComponent(normalizedPath)}`,
        method: 'DELETE'
      });
      setSnackbar({ open: true, severity: 'success', message: 'ההסבר נמחק' });
      setEditOpen(false);
      setHelp(null);
    } catch (error) {
      setSnackbar({ open: true, severity: 'error', message: 'מחיקה נכשלה' });
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Box
          sx={{
            borderRadius: '50px',
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.contrastText',
            mx: 'auto',
            mb: 0,
            py: 0.5,
            textAlign: 'center',
            boxSizing: 'border-box'
          }}
        >
          <Box sx={{ fontWeight: 700, color: 'black', fontSize: { xs: '1rem', md: '1.5rem' } }}>{pageTitle}</Box>
        </Box>

        {/* Only next to a title — while the page (and its name) is still loading, show nothing. */}
        {pageTitle && (
          <Box
            sx={{
              position: 'absolute',
              top: '35%',
              right: -15,
              transform: 'translateY(-50%)'
            }}
          >
            <Tooltip title="מה יש כאן?" color="primary.main">
              <IconButton shape="circle" color="secondary" size="small" onClick={handleOpen} aria-label="מה יש כאן?">
                <Box sx={{ fontSize: 12, fontWeight: 'bold' }}>i</Box>
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            p: 3,
            maxWidth: 1000,

            borderRadius: 1,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            border: '1px solid',
            borderColor: 'grey.200',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)'
              }}
            />
            <Box sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'primary.main' }}>מה יש כאן?</Box>
          </Box>
          {userPermission?.isAdmin && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="עריכת הסבר">
                <IconButton shape="circle" color="primary" size="medium" onClick={onEditClick} aria-label="עריכת הסבר">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {help && (
                <Tooltip title="מחיקת הסבר">
                  <IconButton shape="circle" color="error" size="medium" onClick={handleDelete} aria-label="מחיקת הסבר">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            backgroundColor: 'common.white',
            borderRadius: 0,
            p: 1,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 3
            }
          }}
        >
          {help?.content ? (
            <Box
              sx={{
                border: '1px solid #eee',
                borderRadius: 1,
                px: 2,
                minHeight: 100,
                overflow: 'auto',
                maxHeight: '70vh',
                '& img': {
                  maxWidth: '900px',
                  maxHeight: '600px',
                  width: 'auto',
                  height: 'auto',
                  display: 'block',
                  objectFit: 'contain'
                },
                '& div': {
                  minWidth: 'fit-content'
                }
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: help.content || '' }} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
                fontStyle: 'italic',
                p: 2
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: 'grey.400'
                }}
              />
              אין עדיין הסבר לדף זה.
            </Box>
          )}
        </Box>
      </Popover>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        fullWidth
        maxWidth="md"
        title="עריכת הסבר לדף"
        cancelAction={{ label: 'בטל' }}
        primaryAction={{ label: 'שמור', onClick: handleSave, loading: loading }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2" color="text.primary">
              תוכן ההסבר
            </Typography>
            <EditorComponent value={editContent} setValue={setEditContent} height={300} />
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default PageTitleHeader;
